<template>
  <main class="md:mx-14 mx-4 pt-8">
    <div class="flex items-center gap-x-4 lg:gap-x-12">
      <div
        class="flex items-center border-main rounded-lg shadow-xs w-20 gap-x-2 text-gray-90 border-gray-90 px-1 py-1.5 lg:py-2 font-semibold text-fg-default text-xs cursor-pointer justify-center"
        @click="goBack"
      >
        <i class="el-icon-back"></i>
        <p class="font-light text-xs">Back</p>
      </div>
      <div class="flex items-center">
        <bulk-payout-icon-2 />
        <span class="text-lg font-light mx-3">
          <arrow-right-icon class="h-4" />
        </span>
        <p class="text-black-10 font-medium text-xs lg:text-sm capitalize">
          New Payout
        </p>
        <span class="text-lg font-light ml-4 mr-3">
          <arrow-right-icon class="h-4" />
        </span>
        <p class="text-black-10 font-medium text-xs lg:text-sm capitalize">
          Confirm Beneficiary
        </p>
      </div>
    </div>

    <!-- Payout Title -->
    <div class="mt-8 pb-8 border-b">
      <p class="text-fg-muted font-normal text-sm mb-3">Payout Name</p>
      <div class="sm:flex sm:justify-between">
        <div class="mb-3 md:mb-0">
          <div class="mb-3 flex items-center capitalize gap-2">
            <h2 class="text-xl">{{ bulkPayoutDetails.items.remarks }} -</h2>
            <currency-icons
              class="ml-2"
              size="h-5 w-5 lg:h-6 lg:w-6"
              :currency="
                getCurrencyCountry(
                  bulkPayoutDetails.items.currency || $CURRENCY.NGN
                )
              "
            />
            <span class="text-sm text-black-10">
              {{ bulkPayoutDetails.items.currency }}
              ({{ isRecurring ? 'Recurring' : 'One Time' }})
            </span>
          </div>
          <p class="flex items-center mt-3">
            <span class="text-xs lg:text-sm text-black-10 font-bold mr-1"
              >Payout ID:</span
            >
            <span class="flex items-center gap-x-1"
              ><span class="text-xs lg:text-sm font-light truncate">{{
                maskID(bulkPayoutDetails.items.id) || '-'
              }}</span>
              <span
                v-if="bulkPayoutDetails.items.id"
                class="justify-center cursor-pointer flex items-center"
                v-clipboard:copy="'7787678'"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
              >
                <copy-icon /> </span
            ></span>
          </p>
          <div></div>
        </div>

        <button
          class="mt-3 sm:mt-0 text-sm sm:text-base px-5 py-3 rounded-lg shadow-xs bg-primary text-white flex items-center gap-2"
          @click="openModal('showConfirmPayout')"
        >
          <check-in-circle />Confirm Payout
        </button>
      </div>
    </div>

    <!-- Payout Details -->
    <div class="flex py-8 pb-8 border-b">
      <div class="w-1/2">
        <p class="text-fg-muted text-sm font-normal">Total Amount</p>
        <h4 class="text-fg-default lg:text-base text-xs font-semibold my-1.5">
          {{
            parseMoneyAll(
              parseInt(bulkPayoutDetails.items.totalAmount) || 0,
              bulkPayoutDetails.items.currency || this.$CURRENCY.NGN
            )
          }}
        </h4>
        <p
          v-if="bulkPayoutDetails.items.fee"
          class="font-normal text-fg-muted text-xs lg:text-sm"
        >
          (Fees -
          {{
            parseMoneyAll(
              parseInt(bulkPayoutDetails.items.fee.amount),
              bulkPayoutDetails.items.fee.currency
            )
          }}
          )
        </p>
      </div>
      <div class="w-1/2 flex flex-col gap-5">
        <p class="text-fg-muted text-sm font-normal">Date Created</p>
        <h4 class="font-normal text-sm text-black-10">
          {{ $dayjs().format('hh:MM A, MMM DD, YYYY') }}
        </h4>
      </div>
    </div>

    <!-- Beneficiary Details -->
    <div class="mt-8">
      <div v-if="isSinglePayout">
        <h2 class="mt-8 mb-6 text-xl">Beneficiary Details</h2>
        <div class="bg-gray-30 md:py-8 md:px-11 px-4 py-5 rounded-lg">
          <div
            class="bg-white rounded-lg md:px-8 md:pt-8 px-3 pt-3 flex flex-wrap w-full"
          >
            <div
              v-for="(beneficiary, index) in payoutDetails"
              :key="index"
              :class="{ 'w-1/2 sm:w-1/3 2xl:w-1/4': beneficiary.value }"
            >
              <div v-if="beneficiary.value" class="w-full mb-11">
                <p class="font-bold text-black-10 text-13px mb-2">
                  {{ beneficiary.title }}
                </p>
                <p class="font-normal text-sm text-black-10 capitalize">
                  {{ beneficiary.value || '-' }}
                </p>
              </div>
            </div>
          </div>

          <!-- intermediary bank details -->
          <div
            v-if="hasIntermediaryBankDetails"
            class="bg-white rounded-lg mt-5 px-8 pt-8"
          >
            <p class="font-normal text-sm lg:text-lg pb-4">
              Intermediary Bank Details
            </p>
            <div class="flex flex-wrap w-full">
              <div
                v-for="(details, index) in intermediaryBankDetailsStruct"
                :key="index"
                :class="{ 'w-1/2 sm:w-1/3 2xl:w-1/4': details.value }"
              >
                <div v-if="details.value" class="w-full mb-11">
                  <p class="font-bold text-black-10 text-13px mb-2">
                    {{ details.title }}
                  </p>
                  <p class="font-normal text-sm text-black-10 capitalize">
                    {{ details.value || '-' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <beneficiary-table
        v-if="isMultiplePayout"
        :isLoading="isLoadingTable"
        :table-data="bulkPayoutDetails.attributes || []"
        table-type="history"
        :page="bulkPayoutDetails.page || {}"
        @sort="sortHandler"
        @handleCurrentChange="handleCurrentChange"
        @refresh="refreshHandler"
      />
    </div>

    <!-- Dialog -->
    <confirm-payout
      v-if="showConfirmPayout"
      :showDialog="showConfirmPayout"
      @closeDialog="closeModal('showConfirmPayout')"
      @handleConfirmBulkPayout="handleConfirmPayout"
    />
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeModal('showVerifyAuthDialog')"
      @confirmBulkPayout="confirmBulkPayoutHandler"
    />
    <payout-in-progress-dialog
      :showDialog="showPayoutInProgressDialog"
      @closeDialog="completePayout('showPayoutInProgressDialog')"
      @saveBeneficiary="
        displayAnotherModal(
          'showSaveBeneficiaryDialog',
          'showPayoutInProgressDialog'
        )
      "
    />
    <save-beneficiary-dialog
      :showDialog="showSaveBeneficiaryDialog"
      @closeDialog="completePayout('showSaveBeneficiaryDialog')"
    />
  </main>
</template>

<script>
import bulkPayout from '@/mixins/bulkPayout.js'
import { MaskID, ParseMoneyAll, GetCurrencyCountry } from '@/utils/helpers'
export default {
  mixins: [bulkPayout],
  components: {
    BulkPayoutIcon2: () => import('@/assets/v2/icons/BulkPayoutIcon2.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    CheckInCircle: () => import('@/assets/v2/icons/CheckInCircle.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    ArrowRightIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog.vue'),
    ConfirmPayout: () =>
      import('@/components/v2/bulkPayout/dialogs/ConfirmPayout.vue'),
    PayoutInProgressDialog: () =>
      import('@/components/v2/bulkPayout/dialogs/PayoutInProgressDialog.vue'),
    SaveBeneficiaryDialog: () =>
      import('@/components/v2/bulkPayout/dialogs/SaveBeneficiaryDialog.vue'),
    BeneficiaryTable: () =>
      import('@/components/v2/bulkPayout/tables/BeneficiaryTable.vue')
  },
  data () {
    return {
      showConfirmPayout: false,
      showPayoutInProgressDialog: false,
      showSaveBeneficiaryDialog: false,
      showVerifyAuthDialog: false,
      authState: '',
      twoFaCode: null,
      isLoading: false,
      isLoadingTable: false,
      payload: {
        page: 1,
        size: 10,
        id: ''
      }
    }
  },
  computed: {
    uploadedFileUrls () {
      return this.$store.state.bulkPayout.uploadedFileUrls
    },
    beneficiaryId () {
      return this.$store.state.bulkPayout.beneficiaryDetails.id
    },
    bulkPayoutDetails () {
      return this.$store.state.bulkPayout.currentBulkPayoutDetails
    },
    isSinglePayout () {
      return this.bulkPayoutDetails.items.beneficiaryType === 'single'
    },
    isMultiplePayout () {
      return this.bulkPayoutDetails.items.beneficiaryType === 'multiple'
    },
    isRecurring () {
      return this.bulkPayoutDetails.items.isRecurring
    },
    hasIntermediaryBankDetails () {
      return this.bulkPayoutDetails.attributes.details.intermediaryBank
    },
    selectedMultiBeneficiaries () {
      return this.$store.state.bulkPayout.selectedBeneficiaries || []
    },
    payoutDetails () {
      const details = this.bulkPayoutDetails.attributes.details?.bankDetails
        ? this.bulkPayoutDetails.attributes.details?.bankDetails
        : this.bulkPayoutDetails.attributes.details
      return [
        {
          title: 'Beneficiary',
          value: details.accountName
        },
        {
          title: 'Account Number',
          value: details.accountNumber
        },
        {
          title: 'Bank Name',
          value: details.bankName
        },
        {
          title: 'Country',
          value: details.bankCountry
        },
        {
          title: 'Swift Code',
          value: details.swiftCode
        },
        {
          title: 'Routing Number',
          value: details.routingNumber
        }
      ]
    },
    intermediaryBankDetailsStruct () {
      return [
        {
          title: 'Bank Name',
          value:
            this.bulkPayoutDetails.attributes.details.intermediaryBank.bankName
        },
        {
          title: 'Bank Reference',
          value:
            this.bulkPayoutDetails.attributes.details.intermediaryBank.reference
        },
        {
          title: 'Swift Code/Routing No',
          value:
            this.bulkPayoutDetails.attributes.details.intermediaryBank.swiftCode
        },
        {
          title: 'Bank Address',
          value:
            this.bulkPayoutDetails.attributes.details.intermediaryBank
              .bankAddress
        }
      ]
    }
  },
  methods: {
    goBack () {
      this.$router.go(-1)
    },
    maskID (id) {
      return MaskID(id)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Payout ID copied successfully!'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    closeModal (arg) {
      this[arg] = false
    },
    openModal (arg) {
      this[arg] = true
    },
    displayAnotherModal (opened, closed) {
      this.openModal(opened)
      this.closeModal(closed)
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    handleConfirmPayout () {
      this.authState = 'confirmBulkPayout'
      this.displayAnotherModal('showVerifyAuthDialog', 'showConfirmPayout')
    },
    completePayout (modal) {
      this.$store
        .dispatch('bulkPayout/setActiveComponent', 'bulk-payout-overview')
        .then(() => {
          this.$router.push('/payout')
          this.$store.dispatch('bulkPayout/resetBulkPayoutState')
          this.closeModal(modal)
        })
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    handleCurrentChange (payload) {
      this.payload.page = payload.page
      this.getBulkPayout('table')
    },
    refreshHandler () {
      this.getBulkPayout('mounted')
    },
    async initiateSinglePayout () {
      const payload = {
        currency: this.bulkPayoutDetails.items.currency,
        remarks: this.bulkPayoutDetails.items.remarks,
        twoFaCode: this.twoFaCode,
        beneficiaryID: this.beneficiaryId,
        beneficiaryType: this.bulkPayoutDetails.items.beneficiaryType,
        isRecurring: this.bulkPayoutDetails.items.isRecurring,
        amount: parseFloat(this.bulkPayoutDetails.items.totalAmount),
        payoutType:
          this.bulkPayoutDetails.attributes.details.payoutType || 'banks',
        ...(this.isRecurring && {
          scheduleDetails: {
            ...this.bulkPayoutDetails.items.scheduleDetails
          }
        }),
        ...(this.uploadedFileUrls.length && {
          invoices: this.uploadedFileUrls
        })
      }
      this.$store
        .dispatch('bulkPayout/initiateSinglePayout', payload)
        .then(() => {
          this.displayAnotherModal(
            'showPayoutInProgressDialog',
            'showVerifyAuthDialog'
          )
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.closeModal('showVerifyAuthDialog')
        })
    },
    async initiatePayout () {
      const selectedBeneficiaries = this.bulkPayoutDetails.attributes.map(
        (item) => ({
          amount: item.amount.amount,
          beneficiaryID: item.id
        })
      )
      const payload = {
        twoFaCode: this.twoFaCode,
        currency: this.bulkPayoutDetails.items.currency,
        remarks: this.bulkPayoutDetails.items.remarks,
        beneficiaryType: this.bulkPayoutDetails.items.beneficiaryType,
        isRecurring: this.bulkPayoutDetails.items.isRecurring,
        amount: this.bulkPayoutDetails.items.totalAmount,
        payoutType: this.bulkPayoutDetails.items.payoutType,
        scheduleDetails: this.bulkPayoutDetails.items.scheduleDetails,
        accounts: selectedBeneficiaries,
        ...(this.uploadedFileUrls.length && {
          invoices: this.uploadedFileUrls
        })
      }

      const vm = this
      await this.$store
        .dispatch('bulkPayout/initiateSinglePayout', payload)
        .then(() => {
          this.$store.dispatch(
            'bulkPayout/setActiveComponent',
            'bulk-payout-overview'
          )
          this.$store.dispatch('bulkPayout/removeSelectedBeneficiaries', [])
          this.$router.push('/payout')
          vm.$message({
            showClose: true,
            message: 'Confirmed successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.closeModal('showVerifyAuthDialog')
        })
    },
    async initiateBulkPayout () {
      const vm = this
      const formData = this.$store.state.bulkPayout.bulkPayoutFormData
      formData.delete('twoFaCode')
      formData.append('twoFaCode', this.twoFaCode)

      await this.$store
        .dispatch('bulkPayout/initiateBulkPayout', formData)
        .then(() => {
          this.$store.dispatch(
            'bulkPayout/setActiveComponent',
            'bulk-payout-overview'
          )
          this.$router.push('/payout')
          vm.$message({
            showClose: true,
            message: 'Confirmed successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.closeModal('showVerifyAuthDialog')
        })
    },
    async confirmBulkPayoutHandler (twoFaCode) {
      this.twoFaCode = twoFaCode.code
      if (this.bulkPayoutDetails.items.beneficiaryType === 'single') {
        await this.initiateSinglePayout()
      } else {
        if (this.selectedMultiBeneficiaries.length > 0) {
          await this.initiatePayout()
        } else {
          await this.initiateBulkPayout()
        }
      }
    },
    async getBulkPayout () {
      const functionCaller =
        caller === 'mounted' ? 'isLoading' : 'isLoadingTable'
      this[functionCaller] = true

      await this.$store
        .dispatch('bulkPayout/getBulkPayout', this.payload)
        .then(() => {
          this[functionCaller] = false
        })
        .catch((error) => {
          this[functionCaller] = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this[functionCaller] = false
        })
    },
    sortHandler (sortObj) {
      if (sortObj.sortFormat === 'amount') {
        this.$store.dispatch(
          'bulkPayout/sortBeneficiariesAmountData',
          sortObj.direction
        )
      } else if (sortObj.sortFormat === 'name') {
        this.$store.dispatch(
          'bulkPayout/sortBeneficiariesNameData',
          sortObj.direction
        )
      }
    }
  }
}
</script>
